import { ApiError } from '@discovr/common';
import type { ImportTarget } from '@discovr/types';
import { campaignCopy } from './campaignCopy';

/**
 * Common error-message helper. Builds on `humanizeApiError` from
 * `@discovr/common`, but provides a single place to layer
 * domain-specific overrides if/when needed.
 *
 * Pattern: `errorMessage(err, fallback)` returns the first non-empty
 * string in this priority:
 *   1. Readable copy for known ApiError codes (CODE_MESSAGES)
 *   2. The thrown Error's `.message` (when meaningful)
 *   3. The caller's `fallback` string
 *   4. A generic "Something went wrong" sentinel
 */

// Codes whose server text is not fit for a toast; shared by every write mutation.
const CODE_MESSAGES: Readonly<Record<string, string>> = {
  POST_ON_HOLD: campaignCopy.creator.hold.heldActionReason,
  STAGE_CHANGED: campaignCopy.creator.rollback.stageChanged,
  NOT_ROLLBACKABLE: campaignCopy.creator.rollback.notRollbackable,
  STAGE_DISABLED: campaignCopy.creator.rollback.stageDisabled,
  PRODUCTS_LOCKED: campaignCopy.creator.previousProducts.locked,
  PROFILE_UNOFFERABLE: campaignCopy.creator.rollback.profileUnofferable,
  CONFIRM_MISMATCH: campaignCopy.typedConfirm.prompt,
  NO_HANDLES: 'No handles found. Paste at least one Instagram handle or profile link.',
  REQUEST_IN_PROGRESS: 'This is still being processed. Wait a moment, then refresh.',
  BAD_GATEWAY: "Couldn't reach the service. Try again.",
  // Creator import refusals the admin can fix (backend import_refusal_codes.go).
  IMPORT_FILE_TOO_LARGE: 'This file is too large. Split it into smaller files and upload them one at a time.',
  IMPORT_SHEET_TOO_LARGE_TO_REVIEW: 'This sheet is too big to review in one go. Split it into smaller files and upload them one at a time.',
  IMPORT_TOO_MANY_ROWS: 'This sheet has more rows than one import takes. Split it into smaller files.',
  IMPORT_UNSUPPORTED_FILE: 'Upload a .xlsx or .csv file.',
  IMPORT_UNREADABLE_FILE: "This file couldn't be read. Re-save it as .xlsx or CSV UTF-8 and try again.",
  IMPORT_NO_DATA_SHEET: 'No sheet with creator rows was found in this file. Check that a sheet has a header row with creators under it.',
  IMPORT_NO_DATA_ROWS: 'This sheet has a header row but no creator rows under it.',
  IMPORT_LONG_BLANK_RUN: 'This sheet has a very long run of empty rows. Delete them and upload it again.',
  IMPORT_DUPLICATE_HEADER: 'Two columns in this sheet have the same header. Rename one and upload it again.',
  IMPORT_UNSUPPORTED_PLATFORM:
    "This campaign has no Instagram or YouTube platform set, so a sheet can't be imported. Set the platform first.",
  IMPORT_OUTREACH_NOT_INSTAGRAM: "Outreach only imports Instagram creators, and this campaign isn't on Instagram.",
  IMPORT_MAIN_COLUMN_UNMAPPED: 'Map at least one main deliverable column: creators new to this campaign need a price.',
  IMPORT_USAGE_DAYS_NEED_PRICE_COLUMN:
    'Map the Usage Rights price column too: usage days need a price in the same row.',
  IMPORT_ROWS_NEED_FIXING: 'Some rows still need fixing. Fix them, or skip them and import the rest.',
  IMPORT_NOTHING_TO_IMPORT: 'There is nothing left to import. Every row was removed or skipped.',
  IMPORT_TOO_MANY_CHANGES: 'That is too many changes to save at once. Save fewer rows at a time.',
};

// Generic HTTP codes that only mean this in the outreach import; scoped by
// target so the same codes elsewhere keep the server's own text.
const OUTREACH_IMPORT_CODE_MESSAGES: Readonly<Record<string, string>> = {
  FORBIDDEN: "You don't have access to add creators to outreach.",
  SERVICE_UNAVAILABLE: "Outreach isn't set up for this campaign yet.",
};

/** Readable copy for a known ApiError code, or undefined; never the server's own text. */
export function knownErrorCopy(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || !Object.hasOwn(CODE_MESSAGES, err.code)) return undefined;
  return CODE_MESSAGES[err.code];
}

const DUPLICATE_HEADER_CODE = 'IMPORT_DUPLICATE_HEADER';
const MAX_HEADER_CHARS = 60;
const HTTP_PAYLOAD_TOO_LARGE = 413;
const HTTP_SERVER_ERROR_FROM = 500;
const NETWORK_ERROR_CODE = 'NETWORK_ERROR';
const TIMEOUT_PATTERN = /timeout|timed out/i;
export const IMPORT_SERVER_FAILURE_COPY = 'Something went wrong on our side — try again in a moment.';
export const IMPORT_PAYLOAD_TOO_LARGE_COPY =
  'This file is too large (the limit is 5 MB). Split it into smaller files and upload them one at a time.';

// The refusal names the repeated header when the server says which one.
function duplicateHeaderCopy(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || err.code !== DUPLICATE_HEADER_CODE) return undefined;
  if (typeof err.details !== 'object' || err.details === null || !('header' in err.details)) return undefined;
  const header = typeof err.details.header === 'string' ? err.details.header.trim() : '';
  if (header === '') return undefined;
  const shown = header.length > MAX_HEADER_CHARS ? `${header.slice(0, MAX_HEADER_CHARS)}…` : header;
  return `Two columns in this sheet are both called "${shown}". Rename one and upload it again.`;
}

/** Readable copy for a failed import request: the shared codes, plus the
 *  outreach-only wording for a missing permission or an unconfigured outreach. */
export function importErrorCopy(err: unknown, target: ImportTarget): string | undefined {
  const shared = duplicateHeaderCopy(err) ?? knownErrorCopy(err);
  if (shared !== undefined || target !== 'outreach') return shared;
  if (!(err instanceof ApiError) || !Object.hasOwn(OUTREACH_IMPORT_CODE_MESSAGES, err.code)) return undefined;
  return OUTREACH_IMPORT_CODE_MESSAGES[err.code];
}

export function errorMessage(err: unknown, fallback?: string): string {
  const mapped = knownErrorCopy(err);
  if (mapped) return mapped;
  if (err instanceof Error && err.message) return err.message;
  if (fallback) return fallback;
  return 'Something went wrong';
}

/** A failed upload, worded by cause: a known refusal keeps its own copy; a
 *  file the proxy turned away is too large; a server error or a request that
 *  timed out is ours to fix, not the file's. A request with no response for
 *  any other reason is left to the caller (usually a blocked upload). */
export function importUploadErrorCopy(err: unknown, target: ImportTarget): string | undefined {
  const known = importErrorCopy(err, target);
  if (known !== undefined || !(err instanceof ApiError)) return known;
  if (err.httpStatus === HTTP_PAYLOAD_TOO_LARGE) return IMPORT_PAYLOAD_TOO_LARGE_COPY;
  if (err.httpStatus >= HTTP_SERVER_ERROR_FROM) return IMPORT_SERVER_FAILURE_COPY;
  if (err.code === NETWORK_ERROR_CODE && TIMEOUT_PATTERN.test(err.message)) return IMPORT_SERVER_FAILURE_COPY;
  return undefined;
}
